import React from "react";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { PurpleSwitch } from "../../../helpers/helpers";
import { generateTimeOptions, get_t_str, text_to_num } from "../../../helpers/helpers";
import DatePickerDropdown from "../requests/shared/helpers/DatePickerDropdown";
import {
  buildInheritedFutureRecurringHoursRow,
} from "./futureRecurringHoursHelpers";

const dayOfWeekFullNameLookup = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

function FutureRecurringHoursGroupInput({
  group,
  maxStartQhnum,
  minEffectiveStartDate,
  blackoutDates,
  standardOperatingHours,
  storeId,
  futureRecurringHours,
  onDelete,
  onEffectiveStartDateChange,
  onToggleDay,
  onTimeChange,
}) {
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  const datePickerRef = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    }

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  const displayRows = React.useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) => {
        const dow = index + 1;
        return (
          group.rows.find((row) => row.dow === dow) ||
          buildInheritedFutureRecurringHoursRow({
            futureRecurringHours,
            storeId,
            effectiveStartDate: group.effective_start_date,
            dow,
            standardOperatingHours,
          })
        );
      }),
    [
      futureRecurringHours,
      group.effective_start_date,
      group.rows,
      standardOperatingHours,
      storeId,
    ]
  );

  return (
    <div className="w-full bg-slate-50 rounded-xl p-4">
      <div className="w-full flex items-center gap-x-2">
        <DatePickerDropdown
          selectedDate={group.effective_start_date}
          setDate={(newDate) => {
            onEffectiveStartDateChange(newDate);
            setShowDatePicker(false);
          }}
          minEffectiveDate={minEffectiveStartDate}
          blackoutDates={blackoutDates}
          showDailyDatePicker={showDatePicker}
          toggleDailyDatePicker={() => setShowDatePicker((prevState) => !prevState)}
          datePickerRef={datePickerRef}
        />
        <div>
          <div
            className="w-10 h-10 bg-slate-100 hover:bg-slate-200 flex items-center justify-center rounded-md text-rose-800 cursor-pointer"
            onClick={onDelete}
          >
            <DeleteOutlineOutlinedIcon />
          </div>
        </div>
      </div>
      <div className="mt-4">
        {displayRows.map((day) => {
          const isOpen = day.open_time !== 0 || day.close_time !== 0;

          return (
            <div
              key={`${group.effective_start_date}-${day.dow}`}
              className="flex items-center justify-between mt-4"
            >
              <div className="flex items-center">
                <PurpleSwitch
                  size="small"
                  checked={isOpen}
                  onChange={() => onToggleDay(day.dow)}
                />
                <p className="text-slate-900 text-xs ml-1">
                  {dayOfWeekFullNameLookup[day.dow]}
                </p>
              </div>
              {!isOpen ? (
                <div className="h-[34px] flex items-center">
                  <p className="text-slate-900">Closed</p>
                </div>
              ) : (
                <div className="flex items-center gap-x-1">
                  <div className="w-[100px]">
                    <Select
                      fullWidth
                      value={get_t_str(day.open_time)}
                      name="open_time"
                      onChange={(event) =>
                        onTimeChange(day.dow, "open_time", text_to_num(event.target.value))
                      }
                      size="small"
                      sx={{
                        borderRadius: "8px",
                        paddingY: "0px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                        fontSize: "12px",
                      }}
                    >
                      {generateTimeOptions(maxStartQhnum).map((option, index) => (
                        <MenuItem key={index} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  <div className="text-slate-500 flex items-center justify-center">
                    <ArrowForwardIcon style={{ fontSize: "16px" }} />
                  </div>
                  <div className="w-[100px]">
                    <Select
                      fullWidth
                      value={get_t_str(day.close_time)}
                      name="close_time"
                      onChange={(event) =>
                        onTimeChange(day.dow, "close_time", text_to_num(event.target.value))
                      }
                      size="small"
                      sx={{
                        borderRadius: "8px",
                        paddingY: "0px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                        fontSize: "12px",
                      }}
                    >
                      {generateTimeOptions(maxStartQhnum).map((option, index) => (
                        <MenuItem key={index} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FutureRecurringHoursGroupInput;
